import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { toLbForStorage, convertWeight } from "@/lib/format";

export default function HarvestFormDialog({ hives, defaultYear, event, harvest, onSaved }) {
  const { user: prefs } = useAuth();
  const unit = prefs?.weight_unit || "lb";
  const isEdit = !!harvest;
  const today = new Date().toISOString().slice(0, 10);
  const eventDate = event?.due_date?.slice(0, 10);
  const eventYear = eventDate ? Number(eventDate.slice(0, 4)) : null;

  const [form, setForm] = useState({
    hive_id: harvest?.hive_id || event?.hive_id || hives[0]?.id || "",
    year: harvest?.year || eventYear || defaultYear || new Date().getFullYear(),
    amount: harvest ? String(Math.round(convertWeight(harvest.amount_lb, prefs) * 100) / 100) : "",
    product_type: harvest?.product_type || "honey",
    harvest_date: harvest?.harvest_date?.slice(0, 10) || eventDate || today,
    notes: harvest?.notes ?? event?.notes ?? "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        year: Number(form.year),
        amount_lb: toLbForStorage(form.amount, prefs),
        product_type: form.product_type,
        harvest_date: form.harvest_date || null,
        notes: form.notes,
      };
      if (isEdit) {
        await api.patch(`/harvests/${harvest.id}`, body);
        toast.success("Harvest updated");
      } else {
        await api.post("/harvests", { hive_id: form.hive_id, ...body, event_id: event?.id || null });
        toast.success(event ? "Harvest logged & reminder closed" : "Harvest logged");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (hives.length === 0) {
    return (
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium">No hives yet</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-stone-600">Add a hive first, then come back to log your harvest.</p>
      </DialogContent>
    );
  }

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle className="font-display text-2xl font-medium">
          {isEdit ? "Edit harvest" : event ? `Log harvest · ${event.title}` : "Log harvest"}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label>Hive</Label>
          <Select value={form.hive_id} onValueChange={(v) => setForm({ ...form, hive_id: v })} disabled={isEdit}>
            <SelectTrigger data-testid="harvest-hive-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {hives.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>Amount ({unit})</Label>
            <Input
              type="number"
              step="0.1"
              min="0"
              max="10000"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              placeholder={unit === "kg" ? "19.0" : "42.5"}
              data-testid="harvest-amount-input"
              autoFocus
            />
          </div>
          <div>
            <Label>Product</Label>
            <Select value={form.product_type} onValueChange={(v) => setForm({ ...form, product_type: v })}>
              <SelectTrigger data-testid="harvest-product-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="honey">Honey</SelectItem>
                <SelectItem value="wax">Wax</SelectItem>
                <SelectItem value="propolis">Propolis</SelectItem>
                <SelectItem value="pollen">Pollen</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Year</Label>
            <Input type="number" min={1900} max={2100} value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="harvest-year-input" />
          </div>
        </div>
        <div>
          <Label>Date (optional)</Label>
          <Input type="date" value={form.harvest_date} onChange={(e) => setForm({ ...form, harvest_date: e.target.value })} data-testid="harvest-date-input" />
        </div>
        <div>
          <Label>Notes</Label>
          <Textarea rows={2} maxLength={10000} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="harvest-notes-input" />
        </div>
      </div>
      <DialogFooter>
        <Button
          onClick={save}
          disabled={saving || !form.hive_id || !form.amount}
          className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300"
          data-testid="harvest-save-button"
        >
          {saving ? "Saving…" : isEdit ? "Save changes" : event ? "Log & close reminder" : "Save"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
